import NavbarDashboard from 'components/Navbar'
import ModalInbox from 'components/Navbar/ModalInbox'
import NavbarSide from 'components/NavbarSide'
import ScanIndicator from 'components/ScanIndicator'
import { useFetchInboxQuery } from 'features/private-views/dashboard/dashboardApiSlice'
import {
  getInitialNavbarState,
  NAVBAR_STORAGE_KEY,
  setDataInbox,
  toggleModalInbox,
  toggleNavbar,
} from 'features/private-views/dashboard/dashboardSlice'
import {
  refreshSession,
  setAlertMsg,
  toggleAlert,
} from 'features/public-views/login/loginSlice'
import { useAuthAccess } from 'hooks/useAuthAccess'
import useGetMyProfileAvaQuery from 'hooks/useGetMyProfileAvaQuery'
import HomeModal from 'ModalHome/HomeModal'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import ScannerProvider from 'context/Scanner'

function App() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { isOpenNavbar } = useSelector((s) => s.dashboard)
  const [screenWidth, setScreenWidth] = useState(window.innerWidth)
  const [isEffectCompleted, setIsEffectCompleted] = useState(false)
  const { token, alreadychangepass, userNotPartnership, isV3 } = useAuthAccess()
  const profile = useGetMyProfileAvaQuery({ skip: !isEffectCompleted })

  const version = process.env.REACT_APP_VERSION
  const versionLocalStorage = localStorage.getItem('PMB_version')

  const {
    data: dataInbox,
    isSuccess: isSuccessInbox,
    refetch,
  } = useFetchInboxQuery(
    {
      type: 'consent',
    },
    {
      refetchOnMountOrArgChange: true,
    },
  )

  useEffect(() => {
    const prevPath = localStorage.getItem('lastPath')
    if (!prevPath) {
      localStorage.setItem('lastPath', location.pathname)
      dispatch(refreshSession())
    } else {
      if (location?.pathname !== prevPath) {
        localStorage.setItem('lastPath', location.pathname)
        dispatch(refreshSession())
      }
    }
  }, [location.pathname, dispatch])

  useEffect(() => {
    const fetchAndCheckInbox = async () => {
      try {
        const inbox = await refetch().unwrap()

        const foundItem = inbox?.items?.find((e) => {
          try {
            const extra =
              typeof e.extraData === 'string'
                ? JSON.parse(e.extraData)
                : e.extraData
            return extra?.block && !e?.userdoconsent
          } catch {
            return false
          }
        })

        if (foundItem && location?.pathname !== '/snk') {
          dispatch(setDataInbox(foundItem))
          dispatch(toggleModalInbox(true))
        } else {
          dispatch(setDataInbox(null))
          dispatch(toggleModalInbox(false))
        }
      } catch (error) {
        console.error('Gagal refetch inbox:', error)
      }
    }

    fetchAndCheckInbox()
  }, [location.pathname, dispatch, refetch])

  const prevWidthRef = useRef(window.innerWidth)

  useEffect(() => {
    const handleResize = () => {
      const currentWidth = window.innerWidth
      const prevWidth = prevWidthRef.current

      // When transitioning into mobile, close drawer if open (without overwriting desktop/tablet preference)
      if (prevWidth >= 768 && currentWidth < 768) {
        if (isOpenNavbar) {
          dispatch(toggleNavbar(false))
        }
      }
      // When transitioning from mobile to tablet/desktop, restore user preference or default
      else if (prevWidth < 768 && currentWidth >= 768) {
        const targetState = getInitialNavbarState()
        if (isOpenNavbar !== targetState) {
          dispatch(toggleNavbar(targetState))
        }
      }
      // When resizing between tablet and desktop and user hasn't explicitly set a preference, adapt to context default
      else if (
        (prevWidth >= 1100 && currentWidth < 1100 && currentWidth >= 768) ||
        (prevWidth < 1100 && prevWidth >= 768 && currentWidth >= 1100)
      ) {
        try {
          const saved = localStorage.getItem(NAVBAR_STORAGE_KEY)
          if (saved === null) {
            const targetState = currentWidth >= 1100
            if (isOpenNavbar !== targetState) {
              dispatch(toggleNavbar(targetState))
            }
          }
        } catch (e) {
          // ignore localStorage read error
        }
      }

      prevWidthRef.current = currentWidth
      setScreenWidth(currentWidth)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [dispatch, isOpenNavbar])

  useEffect(() => {
    const alertStatus = localStorage.getItem('PMB_alert')

    if (alertStatus === 'true') {
      dispatch(toggleAlert(true))
      dispatch(
        setAlertMsg(
          'Sesi otomatis berakhir karena ada pembaharuan versi aplikasi. Masuk kembali ke akun Anda untuk melanjutkan.',
        ),
      )
      localStorage.removeItem('PMB_alert')
    }
  }, [dispatch])

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)

    const deviceHash = searchParams.get('deviceHash')
    const fcmToken = searchParams.get('fcmToken')
    const mobileVersion = searchParams.get('mobileVersion')
    const packageName = searchParams.get('packageName')
    if (deviceHash) localStorage.setItem('PMB_deviceHash', deviceHash)
    if (fcmToken) localStorage.setItem('PMB_fcmToken', fcmToken)
    if (mobileVersion) localStorage.setItem('PMB_mobileVersion', mobileVersion)
    if (packageName) localStorage.setItem('PMB_packageName', packageName)

    if (versionLocalStorage !== null && versionLocalStorage !== version) {
      localStorage.clear()
      localStorage.setItem('PMB_version', version)
      localStorage.setItem('PMB_alert', 'true')
      window.location.reload()
    } else if (versionLocalStorage === null) {
      localStorage.setItem('PMB_version', version)
    } else if (
      token === null ||
      (token !== null && !userNotPartnership && alreadychangepass === 0)
    ) {
      navigate('/login', { replace: true })
    } else {
      setIsEffectCompleted(true)
    }
  }, [
    token,
    userNotPartnership,
    alreadychangepass,
    versionLocalStorage,
    version,
    isV3,
    navigate,
    dispatch,
  ])

  if (!isEffectCompleted) {
    return null
  }

  const isMobile = screenWidth < 768
  const isTabletOrDesktop = screenWidth >= 768

  return (
    <ScannerProvider>
      <ModalInbox />
      <ScanIndicator />
      <div
        style={{
          paddingTop: '75px',
          paddingBottom: '15px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          position: 'fixed',
          width: '100%',
          height: `calc(var(--vh, 1vh) * 100)`,
        }}
        className="bg-light"
      >
        <NavbarDashboard color="white" expand="md" profile={profile} />

        {/* Sidebar */}
        <NavbarSide />

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            marginLeft: isTabletOrDesktop
              ? isOpenNavbar
                ? '280px'
                : '80px'
              : '0',

            width: isTabletOrDesktop
              ? `calc(100% - ${isOpenNavbar ? '280px' : '80px'})`
              : '100%',
            transition: 'margin-left 0.3s ease',
          }}
          className="bg-light d-flex content-container no-scrollbar"
        >
          <Outlet context={profile} />
        </div>
      </div>
      <HomeModal />
    </ScannerProvider>
  )
}

export default App

